import { Inject, Injectable, Optional } from '@angular/core';
import { SvgLoaderService } from '../svg-loader/svg-loader.service';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { SHOULD_INIT } from '../init.injection-token';
import { v4 as uuidv4 } from 'uuid';

const defsPropertyKey = 'defsElement';

@Injectable()
export class SvgRegistryService {
  private isReadyStateSubject = new BehaviorSubject(false);

  private get defsElement(): HTMLElement {
    return this.document[defsPropertyKey];
  }

  private registry: { [key: string]: SVGSVGElement } = {};

  /** Asynchronously returns state whether the service is initialized */
  isReadyState$(): Observable<boolean> {
    return this.isReadyStateSubject.asObservable();
  }

  constructor(
    private svgLoader: SvgLoaderService,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(SHOULD_INIT) shouldInit: boolean) {
    if (shouldInit) {
      this.initAsync();
    }
  }

  /** Initializes registry by loading svg sprite from SvgLoaderService */
  async initAsync(): Promise<void> {
    if (!this.isReadyStateSubject.value) {
      const sprite = await firstValueFrom(this.svgLoader.getSprite());
      const defsElement = this.document.createElement('defs');
      defsElement.innerHTML = sprite;
      this.document[defsPropertyKey] = defsElement;
      this.isReadyStateSubject.next(true);
    } else {
      throw new Error('The service is already initialized');
    }
  }

  /** Throws error in case an unknown relativePath provided */
  getRequiredSvgElement(relativePath: string): SVGSVGElement {
    const iconFromRegistry = this.getSvgElement(relativePath);

    if (!iconFromRegistry) {
      throw new Error(`Icon with the relative path "${relativePath}" does not exist`);
    }

    return iconFromRegistry;
  }

  /** Returns svg element for provided relativePath */
  getSvgElement(relativePath: string): SVGSVGElement | null {
    const svgElement = this.getOrAddIconToRegistry(relativePath);
    return svgElement ? this.makeUniqueIds(svgElement) : null;
  }

  /** Returns svg element string representation (<svg>...</svg>) for provided relativePath */
  getSvgElementString(relativePath: string): string {
    const svgElement = this.getOrAddIconToRegistry(relativePath);
    return svgElement ? this.makeUniqueIds(svgElement).outerHTML: null;
  }

  /** Returns value indicating whether an icon by the provided relativePath exists */
  doesIconExist(relativePath: string): boolean {
    return !!this.registry[relativePath] || !!this.getOrAddIconToRegistry(relativePath);
  }

  /** Returns all possible icon relative paths */
  getAllIconRelativePaths(): string[] {
    return Array.from(this.defsElement.querySelector('svg').childNodes)
      .filter((element: HTMLElement) => element.tagName === 'svg')
      .map((element: HTMLElement) => {
        return this.convertIdToRelativePath(element.id);
      });
  }

  /** Returns svg element with unique ids of elements */
  private makeUniqueIds(svgElement: SVGSVGElement): SVGSVGElement {
    if (!svgElement) {
      return svgElement;
    }

    const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
    const elementsWithIds = clonedSvg.querySelectorAll('[id]');

    if (elementsWithIds.length) {
      const uniqueSuffix = uuidv4();

      elementsWithIds.forEach((element) => {
        const oldId = element.id;
        const newId = oldId + uniqueSuffix;
        element.id = newId;

        this.replaceUrlReferences(clonedSvg, oldId, newId);
      });
    }

    return clonedSvg;
  }

  private replaceUrlReferences(svgElement: SVGSVGElement, oldId: string, newId: string): void {
    const elements = svgElement.querySelectorAll('[*|href], [style], [fill], [stroke], [filter], [clip-path], [mask], [marker-start], [marker-mid], [marker-end]');

    elements.forEach((element) => {
      Array.from(element.attributes).forEach((attr) => {
        if (attr.value.includes(`url(#${oldId})`)) {
          element.setAttribute(attr.name, attr.value.replace(`url(#${oldId})`, `url(#${newId})`));
        }
      });

      const hrefAttr = element.getAttribute('href') || element.getAttribute('xlink:href');
      if (hrefAttr && hrefAttr.includes(`#${oldId}`)) {
        element.setAttribute(hrefAttr.startsWith('xlink:') ? 'xlink:href' : 'href', hrefAttr.replace(`#${oldId}`, `#${newId}`));
      }
    });
  }

  private getOrAddIconToRegistry(src: string): SVGSVGElement {
    if (!this.registry[src]) {
      try {
        const iconFromDefs = this.defsElement.querySelector(`svg#${this.convertRelativePathToId(src)}`) as SVGSVGElement;
        if (iconFromDefs) {
          this.registry[src] = iconFromDefs;
        } else {
          return null;
        }
      } catch (err) {
        return null;
      }
    }

    return this.registry[src];
  }

  private convertIdToRelativePath(id: string): string {
    return id.split('--').join('/');
  }

  private convertRelativePathToId(relativePath: string): string {
    return relativePath.split('/').join('--');
  }
}
